import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  CIDR_SCOPE_ASSET_CLASSES,
  DOMAIN_SCOPE_ASSET_CLASSES,
  EXTERNAL_SCOPE_ASSET_CLASSES,
} from '@/sections/insights/aiAssistant/components/huntScopeAssetClasses';
import { HuntScopeStep } from '@/sections/insights/aiAssistant/components/HuntScopeStep';

interface HuntScopeTabsProps {
  value: string[];
  onChange: (keys: string[]) => void;
}

const TABS = [
  {
    value: 'assets',
    label: 'Assets',
    classes: EXTERNAL_SCOPE_ASSET_CLASSES,
    searchPlaceholder: 'Search assets...',
    searchAriaLabel: 'Search assets to scope the hunt',
  },
  {
    value: 'domains',
    label: 'Domains',
    classes: DOMAIN_SCOPE_ASSET_CLASSES,
    searchPlaceholder: 'Search domains...',
    searchAriaLabel: 'Search domains to scope the hunt',
  },
  {
    value: 'cidrs',
    label: 'CIDRs',
    classes: CIDR_SCOPE_ASSET_CLASSES,
    searchPlaceholder: 'Search CIDRs...',
    searchAriaLabel: 'Search CIDRs to scope the hunt',
  },
] as const;

/**
 * Tabbed views over ONE shared HuntScopeStep for external hunts: Assets /
 * Domains / CIDRs are class-filtered slices of the same `scope[]`, so a key
 * selected on one tab stays selected everywhere its filter includes it and the
 * combined count is simply `value.length`. This is the D3 "one shared list,
 * filtered views" model — no per-tab local state, no forked SelectionTable.
 *
 * Design (Option 1, underline/boxed tabs + single combined count): a single
 * count line is the sole source of truth (avoids the per-tab-badge redundancy,
 * M7, and the white-on-crimson filled-badge contrast risk, M1). The count is a
 * polite aria-live region so screen-reader users hear the accumulated total
 * change as they toggle rows across tabs (M4).
 */
export function HuntScopeTabs({ value, onChange }: HuntScopeTabsProps) {
  return (
    <Tabs defaultValue="assets" className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <TabsList variant="boxed">
          {TABS.map(t => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              variant="boxed"
              data-testid={`hunt-scope-tab-${t.value}`}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <span
          data-testid="hunt-scope-selected-count"
          aria-live="polite"
          className="text-chariot-text-secondary shrink-0 text-xs"
        >
          {value.length} selected
        </span>
      </div>
      {TABS.map(t => (
        <TabsContent
          key={t.value}
          value={t.value}
          className={cn('mt-0 flex min-h-0 flex-1 flex-col')}
        >
          <HuntScopeStep
            value={value}
            onChange={onChange}
            assetClasses={t.classes}
            searchPlaceholder={t.searchPlaceholder}
            searchAriaLabel={t.searchAriaLabel}
          />
        </TabsContent>
      ))}
    </Tabs>
  );
}
